<template>
  <div class="file-upload">
    <input
      ref="fileInputRef"
      type="file"
      :accept="acceptAttr"
      style="display: none"
      @change="handleFileChange"
    />

    <div
      class="upload-area"
      :class="{ 'has-file': selectedFile, 'drag-over': isDragOver }"
      @click="handleSelectFile"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <div v-if="!selectedFile" class="upload-empty">
        <div class="upload-illustration">
          <img
            src="/images/common/upload-illustration.jpg"
            :alt="$t('upload.alt.uploadImage')"
            class="upload-image"
            draggable="false"
          />
        </div>
        <div class="upload-text">
          <p class="upload-title">{{ $t('upload.title') }}</p>
          <p class="upload-subtitle">{{ supportTextToShow }}</p>
          <p class="upload-limit">{{ $t('upload.maxSize') }}</p>
        </div>
        <button class="upload-button" @click.stop="handleSelectFile">
          {{ $t('upload.choose') }}
        </button>
      </div>

      <div v-else class="file-info">
        <div class="file-icon">
          <Icon :name="getFileIcon(selectedFile)" size="32" />
        </div>
        <div class="file-details">
          <p class="file-name">{{ selectedFile.name }}</p>
          <p class="file-size">
            {{ formatFileSize(selectedFile.size) }}
            <span v-if="pageCount !== undefined" class="page-count"> · {{ pageCount }} {{ $t('upload.pages') }}</span>
          </p>
          <p class="file-type">{{ getFileType(selectedFile) }}</p>
        </div>
        <button class="remove-button" @click.stop="handleRemoveFile">
          <Icon name="material-symbols:close" size="20" />
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="error-message">
      <Icon name="material-symbols:error-outline" size="16" />
      <span>{{ errorMessage }}</span>
    </div>

    <div class="supported-formats">
      <span class="format-label">{{ $t('upload.supported.label') }}</span>
      <span v-for="fmt in formatsToShow" :key="fmt" class="format-item">{{ fmt }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  evaluateFileSelectionContract,
  MAX_UPLOAD_FILE_SIZE,
  resolveFileValidationAction
} from '~/utils/fileUploadValidation.js'

const { t } = useI18n()

const props = withDefaults(defineProps<{
  accept?: string
  allowedExtensions?: string[]
  supportText?: string
  supportedFormats?: string[]
  pageCount?: number
}>(), {
  accept: '.docx,.xlsx,.pptx,.pdf,.epub,.srt,.ass,.vtt',
  allowedExtensions: () => [] as string[],
  supportText: undefined,
  supportedFormats: undefined,
  pageCount: undefined
})

const emit = defineEmits<{
  fileSelected: [file: File]
  fileRemoved: []
  fileTypeMismatch: [file: File]
}>()

const acceptAttr = computed(() => props.accept || '.docx,.xlsx,.pptx,.pdf,.epub,.srt,.ass,.vtt')

const fileInputRef = ref<HTMLInputElement | null>(null)
const selectedFile = ref<File | null>(null)
const isDragOver = ref(false)
const errorMessage = ref('')

const formatsToShow = computed(() => {
  if (props.supportedFormats && props.supportedFormats.length > 0) {
    return props.supportedFormats
  }
  if (props.allowedExtensions && props.allowedExtensions.length > 0) {
    return props.allowedExtensions.map(x => (x.startsWith('.') ? x : `.${x}`))
  }
  return ['.docx', '.xlsx', '.pptx', '.pdf', '.epub', '.srt']
})

const supportTextToShow = computed(() => {
  if (props.supportText) {
    return props.supportText
  }
  if (props.allowedExtensions && props.allowedExtensions.length > 0) {
    const labels = props.allowedExtensions.map(x => x.toUpperCase())
    return `${t('upload.supported.prefix')} ${labels.join('、')} ${t('upload.supported.suffix')}`
  }
  return t('upload.supported.default')
})

const handleSelectFile = () => {
  errorMessage.value = ''
  fileInputRef.value?.click()
}

const validateAndSetFile = (file: File) => {
  if (!file) {
    return
  }

  const contract = evaluateFileSelectionContract({
    file,
    allowedExtensions: props.allowedExtensions,
    maxSizeBytes: MAX_UPLOAD_FILE_SIZE
  })
  const action = resolveFileValidationAction({
    validation: contract.validation,
    file,
    allowedExtensions: props.allowedExtensions
  })

  if (!action.shouldEmitFileSelected) {
    if (action.errorType === 'too_large') {
      errorMessage.value = t('upload.errors.tooLarge', { size: formatFileSize(MAX_UPLOAD_FILE_SIZE) })
      return
    }

    if (action.errorType === 'allowed_extension_mismatch') {
      errorMessage.value = t('upload.errors.unsupportedType', {
        ext: action.ext || '',
        list: props.allowedExtensions.map(x => `.${x}`).join('、')
      })

      if (action.shouldEmitFileTypeMismatch) {
        emit('fileTypeMismatch', file)
      }
      return
    }

    if (action.errorType === 'unsupported_format') {
      errorMessage.value = t('upload.errors.unsupportedFormat')
      return
    }

    errorMessage.value = t('upload.errors.failed', '无法读取文件，请重试或使用点击上传')
    return
  }

  selectedFile.value = file
  errorMessage.value = ''
  emit('fileSelected', file)
}

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    validateAndSetFile(file)
  }
  target.value = ''
}

const handleDragOver = () => {
  if (!isDragOver.value) {
    isDragOver.value = true
  }
}

const handleDragLeave = (event: DragEvent) => {
  const related = event.relatedTarget as Node | null
  const current = event.currentTarget as Node | null
  if (current && related && current.contains(related)) {
    return
  }
  isDragOver.value = false
}

const handleDrop = (event: DragEvent) => {
  isDragOver.value = false

  const transfer = event.dataTransfer
  if (!transfer) {
    return
  }

  if (transfer.files.length > 0) {
    const file = transfer.files[0]
    if (file) {
      validateAndSetFile(file)
    }
    return
  }

  if (transfer.items && transfer.items.length > 0) {
    const item = transfer.items[0]
    if (item && item.kind === 'file') {
      const file = item.getAsFile()
      if (file) {
        validateAndSetFile(file)
        return
      }
    }
  }

  errorMessage.value = t('upload.errors.failed', '无法读取文件，请重试或使用点击上传')
}

const handleRemoveFile = () => {
  selectedFile.value = null
  errorMessage.value = ''
  emit('fileRemoved')
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 B'
  }
  const base = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const index = Math.floor(Math.log(bytes) / Math.log(base))
  return `${(bytes / Math.pow(base, index)).toFixed(1)} ${sizes[index]}`
}

const getFileType = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase()
  const typeMap: Record<string, string> = {
    pdf: 'PDF 文档',
    docx: 'Word 文档',
    xlsx: 'Excel 表格',
    pptx: 'PowerPoint 演示',
    epub: 'EPUB 电子书',
    srt: 'SRT 字幕',
    ass: 'ASS 字幕',
    vtt: 'VTT 字幕'
  }
  return typeMap[extension || ''] || '文档'
}

const getFileIcon = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase()
  const iconMap: Record<string, string> = {
    pdf: 'vscode-icons:file-type-pdf2',
    docx: 'vscode-icons:file-type-word',
    xlsx: 'vscode-icons:file-type-excel',
    pptx: 'vscode-icons:file-type-powerpoint',
    epub: 'vscode-icons:file-type-epub',
    srt: 'vscode-icons:file-type-text',
    ass: 'vscode-icons:file-type-text',
    vtt: 'vscode-icons:file-type-text'
  }
  return iconMap[extension || ''] || 'vscode-icons:file-type-text'
}

const clearFile = () => {
  handleRemoveFile()
}

const setFile = (file: File) => {
  errorMessage.value = ''
  selectedFile.value = file
  emit('fileSelected', file)
}

defineExpose({
  clearFile,
  selectedFile,
  setFile
})
</script>

<style scoped lang="scss">
@use 'sass:color';
@use 'sass:math';

.file-upload {
  width: 100%;
}

.upload-area {
  border: 2px dashed rgba($primary-color, 0.4);
  border-radius: $border-radius-lg;
  padding: $spacing-xl;
  text-align: center;
  background: linear-gradient(
    135deg,
    rgba($white, 0.75) 0%,
    color.adjust($primary-color, $lightness: 42%) 50%,
    rgba($white, 0.75) 100%
  );
  backdrop-filter: blur(12px);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  box-shadow: 0 4px 15px rgba($primary-color, 0.12);

  &:hover,
  &.drag-over {
    border-color: $primary-color;
    background: linear-gradient(
      135deg,
      rgba($white, 0.85) 0%,
      color.adjust($primary-color, $lightness: 35%) 50%,
      rgba($white, 0.85) 100%
    );
    box-shadow: $shadow-md, 0 0 20px rgba($primary-color, 0.18);
    transform: translateY(-2px);
  }

  &.has-file {
    border-color: $success;
    background: linear-gradient(
      135deg,
      rgba($white, 0.88) 0%,
      color.adjust($success, $lightness: 40%) 50%,
      rgba($white, 0.88) 100%
    );
    padding: $spacing-lg;
    box-shadow: 0 4px 15px rgba($success, 0.12);
  }
}

.upload-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $spacing-md;

  .upload-illustration,
  .upload-text {
    pointer-events: none;
  }

  .upload-button {
    pointer-events: auto;
  }
}

.upload-illustration {
  margin-bottom: $spacing-md;

  .upload-image {
    max-width: 200px;
    max-height: 150px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: $border-radius-md;
    transition: transform 0.3s ease, opacity 0.3s ease;
    opacity: 0.9;

    .upload-area:hover & {
      transform: scale(1.05);
      opacity: 1;
    }

    .upload-area.drag-over & {
      transform: scale(1.1);
      opacity: 1;
    }
  }
}

.upload-text {
  .upload-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: $gray-800;
    margin: 0 0 $spacing-xs 0;
  }

  .upload-subtitle {
    font-size: 0.9rem;
    color: $gray-600;
    margin: 0 0 $spacing-xs 0;
  }

  .upload-limit {
    font-size: 0.8rem;
    color: $gray-500;
    margin: 0;
  }
}

.upload-button {
  background-color: $primary-color;
  color: $white;
  border: none;
  border-radius: $border-radius-md;
  padding: $spacing-sm $spacing-lg;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: $primary-dark;
    transform: translateY(-1px);
    box-shadow: $shadow-sm;
  }
}

.file-info {
  display: flex;
  align-items: center;
  gap: $spacing-md;
  text-align: left;
  background: $white;
  padding: $spacing-md;
  border-radius: $border-radius-md;
  box-shadow: $shadow-sm;
}

.file-icon {
  flex-shrink: 0;
}

.file-details {
  flex: 1;

  .file-name {
    font-size: 1rem;
    font-weight: 600;
    color: $gray-800;
    margin: 0 0 $spacing-xs 0;
  }

  .file-size {
    font-size: 0.85rem;
    color: $gray-600;
    margin: 0 0 $spacing-xs 0;
  }

  .file-type {
    font-size: 0.8rem;
    color: $gray-500;
    margin: 0;
  }
}

.remove-button {
  background: none;
  border: none;
  color: $gray-400;
  cursor: pointer;
  padding: $spacing-xs;
  border-radius: $border-radius-sm;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background-color: $gray-100;
    color: $danger;
  }
}

.error-message {
  display: flex;
  align-items: center;
  gap: $spacing-xs;
  margin-top: $spacing-sm;
  padding: $spacing-sm $spacing-md;
  background-color: color.adjust($danger, $lightness: 45%);
  border: 1px solid color.adjust($danger, $lightness: 20%);
  border-radius: $border-radius-md;
  color: $danger;
  font-size: 0.85rem;
}

.supported-formats {
  margin-top: $spacing-md;
  text-align: center;
  font-size: 0.8rem;
  color: $gray-500;

  .format-label {
    margin-right: $spacing-xs;
  }

  .format-item {
    display: inline-block;
    background-color: $gray-100;
    padding: 2px $spacing-xs;
    border-radius: $border-radius-sm;
    margin: 0 math.div($spacing-xs, 2);
    font-family: 'Courier New', monospace;
    font-weight: 500;
  }
}

@media (max-width: 768px) {
  .upload-area {
    padding: $spacing-lg;
  }

  .upload-illustration {
    .upload-image {
      max-width: 150px;
      max-height: 120px;
    }
  }

  .file-info {
    flex-direction: column;
    text-align: center;
    gap: $spacing-sm;

    .file-details {
      order: -1;
    }

    .remove-button {
      align-self: flex-end;
    }
  }

  .supported-formats {
    .format-item {
      margin: math.div($spacing-xs, 2);
    }
  }
}
</style>
